import type { SupabaseClient } from '@supabase/supabase-js';
import { InfrastructureError } from '@dma/shared';
import {
  ScientificEntry,
  type EntryImage,
  type EntryReference,
  type PhysicalProperties,
  type ScientificEntryCategory,
  type ScientificEntryStatus
} from '../domain/entities/ScientificEntry.entity';
import type { ScientificEntryRepository } from '../domain/repositories/ScientificEntryRepository.interface';

const TABLE_NAME = 'scientific_entries';

interface ScientificEntryRow {
  id: string;
  slug: string;
  title: string;
  category: ScientificEntryCategory;
  classification: string;
  chemical_formula: string | null;
  hardness: string | null;
  color: string | null;
  luster: string | null;
  streak: string | null;
  crystal_system: string | null;
  cleavage: string | null;
  fracture: string | null;
  specific_gravity: string | null;
  description: string;
  images: EntryImage[];
  occurrence_locations: string[];
  references_list: EntryReference[];
  status: ScientificEntryStatus;
  created_at: string;
  updated_at: string;
}

function rowToEntity(row: ScientificEntryRow): ScientificEntry {
  const physicalProperties: PhysicalProperties = {
    hardness: row.hardness,
    color: row.color,
    luster: row.luster,
    streak: row.streak,
    crystalSystem: row.crystal_system,
    cleavage: row.cleavage,
    fracture: row.fracture,
    specificGravity: row.specific_gravity
  };

  return ScientificEntry.create({
    id: row.id,
    slug: row.slug,
    title: row.title,
    category: row.category,
    classification: row.classification,
    chemicalFormula: row.chemical_formula,
    physicalProperties,
    description: row.description,
    images: row.images ?? [],
    occurrenceLocations: row.occurrence_locations ?? [],
    references: row.references_list ?? [],
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  });
}

function entityToRow(entity: ScientificEntry): ScientificEntryRow {
  const { props } = entity;
  return {
    id: props.id,
    slug: props.slug,
    title: props.title,
    category: props.category,
    classification: props.classification,
    chemical_formula: props.chemicalFormula,
    hardness: props.physicalProperties.hardness,
    color: props.physicalProperties.color,
    luster: props.physicalProperties.luster,
    streak: props.physicalProperties.streak,
    crystal_system: props.physicalProperties.crystalSystem,
    cleavage: props.physicalProperties.cleavage,
    fracture: props.physicalProperties.fracture,
    specific_gravity: props.physicalProperties.specificGravity,
    description: props.description,
    images: props.images,
    occurrence_locations: props.occurrenceLocations,
    references_list: props.references,
    status: props.status,
    created_at: props.createdAt,
    updated_at: props.updatedAt
  };
}

/**
 * Infrastructure implementation backed by the `scientific_entries` Supabase
 * table (see infra/supabase/migrations/004_create_scientific_entries.sql and
 * infra/supabase/policies/004_scientific_entries_policies.sql). Powers both
 * the public mineralogy/petrology pages (published-only reads) and the admin
 * CRUD screens (all statuses) — RLS on the table enforces that boundary
 * independently of what this class is asked to do, per Security Architecture.
 */
export class SupabaseScientificEntryRepository implements ScientificEntryRepository {
  constructor(private readonly client: SupabaseClient) {}

  public async getPublished(category: ScientificEntryCategory): Promise<ScientificEntry[]> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('category', category)
      .eq('status', 'published')
      .order('title', { ascending: true })
      .returns<ScientificEntryRow[]>();

    if (error) {
      throw new InfrastructureError('SCIENTIFIC_ENTRIES_LIST_FAILED', error.message, { cause: error });
    }
    return (data ?? []).map(rowToEntity);
  }

  public async getPublishedBySlug(
    category: ScientificEntryCategory,
    slug: string
  ): Promise<ScientificEntry | null> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('category', category)
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle<ScientificEntryRow>();

    if (error) {
      throw new InfrastructureError('SCIENTIFIC_ENTRY_FETCH_FAILED', error.message, { cause: error });
    }
    return data ? rowToEntity(data) : null;
  }

  public async getAllForAdmin(category: ScientificEntryCategory): Promise<ScientificEntry[]> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('category', category)
      .order('updated_at', { ascending: false })
      .returns<ScientificEntryRow[]>();

    if (error) {
      throw new InfrastructureError('SCIENTIFIC_ENTRIES_ADMIN_LIST_FAILED', error.message, { cause: error });
    }
    return (data ?? []).map(rowToEntity);
  }

  public async getById(id: string): Promise<ScientificEntry | null> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('id', id)
      .maybeSingle<ScientificEntryRow>();

    if (error) {
      throw new InfrastructureError('SCIENTIFIC_ENTRY_FETCH_FAILED', error.message, { cause: error });
    }
    return data ? rowToEntity(data) : null;
  }

  public async save(entity: ScientificEntry): Promise<void> {
    const { error } = await this.client.from(TABLE_NAME).upsert(entityToRow(entity));
    if (error) {
      throw new InfrastructureError('SCIENTIFIC_ENTRY_SAVE_FAILED', error.message, { cause: error });
    }
  }

  public async delete(id: string): Promise<void> {
    const { error } = await this.client.from(TABLE_NAME).delete().eq('id', id);
    if (error) {
      throw new InfrastructureError('SCIENTIFIC_ENTRY_DELETE_FAILED', error.message, { cause: error });
    }
  }
}
